import {
	Address,
	Name,
	NotificationConfiguration,
	NotificationSettings,
	TableQueryFilter,
	ZomentumPSA,
	ZomentumRoutes
} from "../common";
import {
	AutotaskIntegration,
	ConnectedEmails,
	ConnectWiseIntegration,
	DkimVerification,
	EmailAlias,
	EmailIntegration,
	EtilizeLocale,
	IntegrationMode,
	Integrations,
	KaseyaIntegration,
	Quantifier,
	ZapierIntegration
} from "../Settings";
import { ChildEntityHydrationParams } from "../Utils";
import {
	Block,
	BlockType,
	PricingTableStyles,
	QuoteBlockColorPlacement,
	QuoteBlockHighlightedTotal,
	QuoteLabels,
	RiskMatrixBlockData
} from "../ProposalBuilder/Block";
import { ProductType, UOM } from "../Products";
import { EmailTracking } from "../Others/EmailThread";
import { RadioChangeEvent } from "antd/lib/radio";
import {
	defaultZomentumDocPaperSize,
	MergeTagVariable,
	PaymentOptions,
	DocumentPaperSize
} from "../Documents";
import { VendorCompany } from "../Marketplace/Vendor";
import { VendorUserIntegration } from "../Vendor/User";
import { capitalizeFirstLetter } from "../Utils";
import moment from "moment";

export { PricingTableStyles } from "../ProposalBuilder/Block";

export class CompanyUserCount {
	all = 0;
}

export enum CommissionType {
	PercentageOfSalePrice = "percentage_of_sale_price",
	PercentageOfMargin = "percentage_of_margin",
	SlidingScaleOnSalePrice = "sliding_scale_on_sale_price",
	SlidingScaleOnMargin = "sliding_scale_on_margin"
}

export class DocumentExpirationPreference {
	is_active = false;
	expiration_period = 0;
	warn_expiration = false;
}

export const CommissionLabels = {
	[CommissionType.PercentageOfSalePrice]: "Percentage of Sell Price",
	[CommissionType.PercentageOfMargin]: "Percentage of Margin",
	[CommissionType.SlidingScaleOnSalePrice]: "Sliding Scale On Sell Price",
	[CommissionType.SlidingScaleOnMargin]: "Sliding Scale On Margin"
};

export interface CommissionValues {
	id?: string;
	from?: number;
	to?: number;
	percentage?: number;
}

export interface CommissionFormValues extends CommissionValues {
	commission_type: CommissionType;
	is_enabled: boolean;
	[CommissionType.PercentageOfSalePrice]?: Record<string, CommissionValues>;
	[CommissionType.PercentageOfMargin]?: Record<string, CommissionValues>;
	[CommissionType.SlidingScaleOnSalePrice]?: Record<string, CommissionValues>;
	[CommissionType.SlidingScaleOnMargin]?: Record<string, CommissionValues>;
}

export interface Commission {
	commission_type: CommissionType;
	is_enabled: boolean;
	[CommissionType.PercentageOfSalePrice]?: CommissionValues[];
	[CommissionType.PercentageOfMargin]?: CommissionValues[];
	[CommissionType.SlidingScaleOnSalePrice]?: CommissionValues[];
	[CommissionType.SlidingScaleOnMargin]?: CommissionValues[];
}

export interface UOMMapping {
	item_type: ProductType;
	unit_of_measure: UOM;
}

export class MarketplaceSettings {
	pipeline_id: string | null = "";
}

export enum Languages {
	ENGLISH = "en",
	FRENCH = "fr",
	GERMAN = "de",
	DUTCH = "nl",
	DANISH = "da",
	POLISH = "pl",
	SPANISH = "es",
	ITALIAN = "it"
}
export interface TrialInfoAIWriter {
	readonly is_trial_for_ai_writer_enabled: boolean;
	readonly trial_expire_at: moment.Moment;
}

export interface GoogleDriveTrialConfig {
	readonly integration_enabled: boolean;
	readonly free_drive_folder_id: string;
}

export class CompanySettings {
	currency = "";
	locale = "";
	is2fa_enabled?: boolean;
	is_icecat_integration_enabled?: boolean;
	time_zone = "";
	pricing_table_default_label?: QuoteLabels;
	dkim_verification?: DkimVerification;
	quantifier?: Quantifier;
	document_quantifier?: Quantifier;
	opportunity_quantifier?: Quantifier;
	time_zone_offset?: number | string;
	default_categories?: DefaultProductCategoryMap[] =
		new Array<DefaultProductCategoryMap>();
	commissions?: Commission[];
	date_pattern?: string;
	document_comment_box_labels?: DocumentCommentBoxLabels;
	signature_block_labels?: SignatureBlockLabels;
	default_unit_of_measure_mapping?: UOMMapping[];
	etilize_locale?: EtilizeLocale;
	icecat_locale?: EtilizeLocale;
	sso_settings?: SSOSettings;
	marketplace_settings: MarketplaceSettings = new MarketplaceSettings();
	preference_settings?: PreferenceSettings;
	language?: Languages;
	trial_info_for_ai_writer?: TrialInfoAIWriter | null;
	default_month_for_opportunity_margin?: number;
	doc_sent_count_is_less_than_threshold_to_show_zpayment_banner?: boolean;
	google_drive_trial_config?: GoogleDriveTrialConfig;
}

export interface SSOSettings {
	is_sso_mandatory?: boolean;
	is_sso_enabled?: boolean;
}

export interface DefaultProductCategoryMap {
	readonly item_type: ProductType;
	readonly category_id: string;
}

export enum BillingStatusEnum {
	InTrialNoPaymentMethod = "in_trial_payment_method_not_added",
	InTrial = "in_trial_payment_method_added",
	TrialPaymentOverdue = "in_trial_payment_overdue",
	Active = "active",
	ActivePaymentOverdue = "active_payment_overdue",
	Cancelled = "cancelled",
	Paused = "paused",
	InTrialPaymentMethodAdded = "in_trial_payment_method_added",
	InTrialOnboardingScheduled = "in_trial_onboarding_scheduled"
}

export interface PaymentSource {
	name: string;
	provider: string;
	masked_card_number: string;
	expiry_month: number;
	expiry_year: number;
}

export interface ChargebeePortalSession {
	id: string;
	token: string;
	access_url: string;
	status: string;
	created_at: Date;
	expires_at: Date;
	customer_id: string;
}

export class BillingInfo {
	billing_status: BillingStatusEnum | null = null;
	trial_expiry_days: number | null = 0;
	subscription_renews_on = "";
	is_internal = false;
	current_term_start = "";
	next_invoice_amount = 99;
	payment_source: PaymentSource | null = null;
	request_cancellation_details?: PauseChargebeeSubscriptionRequest;
	should_allow_cancellation? = false;
	should_display_invoices? = false;
	should_display_and_allow_plan_change? = false;
	should_mandate_card_add = false;
	billing_email: string | null = null;

	public static paymentNeeded = (billingInfo: BillingInfo): boolean => {
		return (
			billingInfo?.should_mandate_card_add &&
			!billingInfo?.payment_source &&
			(billingInfo.billing_status ===
				BillingStatusEnum.ActivePaymentOverdue ||
				billingInfo.billing_status ===
					BillingStatusEnum.TrialPaymentOverdue ||
				billingInfo.billing_status ===
					BillingStatusEnum.InTrialNoPaymentMethod)
		);
	};

	public static promptMessageForPayment = (
		billingInfo: BillingInfo
	): string => {
		if (billingInfo?.should_mandate_card_add) {
			switch (billingInfo.billing_status) {
				case BillingStatusEnum.ActivePaymentOverdue:
					return "Subscription payment is overdue, please add a payment method to continue!";
				case BillingStatusEnum.InTrialNoPaymentMethod:
					return "Payment method is not added, please add a payment method to continue!";
				case BillingStatusEnum.TrialPaymentOverdue:
					return "Subscription payment is overdue, please add a payment method to continue!";
				default:
					return "Please add a payment method to continue!";
			}
		} else {
			return "Please add a payment method to continue!s";
		}
	};

	public static isExpired = (billingInfo: BillingInfo | null): boolean => {
		return (
			!!billingInfo &&
			!!billingInfo.billing_status &&
			[
				BillingStatusEnum.InTrialNoPaymentMethod,
				BillingStatusEnum.ActivePaymentOverdue,
				BillingStatusEnum.TrialPaymentOverdue
			].includes(billingInfo.billing_status)
		);
	};

	public static isOnboarded = (billingInfo: BillingInfo): boolean => {
		return (
			billingInfo.billing_status ===
			BillingStatusEnum.InTrialPaymentMethodAdded
		);
	};
	public static isOnboardingCallScheduled = (
		billingInfo: BillingInfo
	): boolean => {
		return (
			billingInfo.billing_status ===
			BillingStatusEnum.InTrialOnboardingScheduled
		);
	};
}

export interface UpdateBillingInfoRequest {
	billing_email: string | null;
}

export class SignupExtraInfo {
	coupon_added_at_signup: string | null = null;
	signup_document_type_selected?: string | null = null;
	after_first_login_redirect_to?: string | null = null;
}

export class UserCompany {
	billing_address: Address | null = new Address();
	created: Date = new Date();
	domain = "";
	sub_domain = "";
	id = "";
	integrations: Integrations = new Integrations();
	logo = "";
	favicon: string | null = "";
	loading_icon: string | null = "";
	name = "";
	integration_mode?: IntegrationMode = IntegrationMode.CRM;
	integrated_psa?: ZomentumPSA | null = null;
	settings: CompanySettings = new CompanySettings();
	updated: Date = new Date();
	users: { data: UserData[] } | null = { data: new Array<UserData>() };
	datePatterns!: string[];
	constraint_modules? = new Array<UserCompanyConstraintModules>();
	is_marketplace_enabled = false;
	signup_extra_info = new SignupExtraInfo();
	about_company = "";
	mcp_agents_enabled = false;
}

export enum UserCompanyConstraintModule {
	SALES_AUTOMATION = "sales_automation",
	APPROVAL_AUTOMATION = "approval_automation",
	REPORT = "report",
	AI_DOCUMENTS = "ai_documents",
	DISTRIBUTORS = "distributors",
	INTEGRATIONS = "integrations",
	DOCUMENTS = "documents"
}

export class UserCompanyConstraintModuleUsageCount {
	initial_balance = 0;
	available = 0;
	total = 0;
}

export class UserCompanyConstraintModules {
	module = "";
	module_usage_count = new UserCompanyConstraintModuleUsageCount();
}

export class DatePattern {
	key = "";
	value = "";
}

export class GetDatePatternsResponse {
	data: string[] = new Array<string>();
	filtered_count_map!: Map<string, number> | null;
	data_with_display_value = new Array<DatePattern>();
}

export class GetUserCompanyRequest {
	included_child_entities: ChildEntityHydrationParams[] =
		new Array<ChildEntityHydrationParams>();
}

export class EditUserCompanyFormValues {
	country = "";
	state = "";
	city = "";
	street = "";
	pincode = "";
	name = "";
	phone = "";
}

export class ChangeCompanyLogoRequest {
	logo = "";

	constructor(logo: string) {
		this.logo = logo;
	}
}

export enum UserRole {
	PrimaryRole = "PARTNER_PRIMARY",
	SecondaryRole = "PARTNER_SECONDARY",
	UserRole = "PARTNER_USER",
	AdminRole = "ADMIN",
	ClientUserRole = "CLIENT_USER",
	ApiUserRole = "PARTNER_API_ACCESS_USER"
}

export enum UserAccess {
	CompanyLevel = "company_level",
	SelfLevel = "self_level"
}

export enum RolePermissionEnum {
	ADMIN = "admin",
	USER = "user",
	MANAGER = "manager",
	API_ACCESS = "api_access"
}

export const UserRolePermissionOrder = [
	"admin",
	"api_access",
	"manager",
	"user"
];

export const AdminRoleOnly = [UserRole.PrimaryRole, UserRole.AdminRole];
export const ManagerAndAdminRoleOnly = [
	UserRole.PrimaryRole,
	UserRole.AdminRole,
	UserRole.SecondaryRole
];

export enum UserPermissionRole {
	CRM_ADMIN = "CRM_ADMIN",
	CPQ_ADMIN = "CPQ_ADMIN",
	USER_MANAGEMENT = "USER_MANAGEMENT"
}

export class UserRolePermissions {
	permissions: string[] = [];
	role = "";
}

export enum DocumentDefaultPreferenceLastUsedEmail {
	LAST_USED_EMAIL_TEXT = "Last used email",
	FIXED_ADDRESS_TYPE = "fixed",
	LAST_USED_ADDRESS_TYPE = "last_used_address"
}

export enum DocumentDefaultPreferenceConfirmationMessage {
	CONFIRMATION_MESSAGE = "You have successfully approved the document. {{Document.Owner.Name}} will contact on further steps"
}

export interface AccessAndRefreshTokenResponse {
	access_token: string;
	refresh_token: string;
}

export class KanabanPreferenceSettings {
	show_terminal_columns = false;
}

export enum TaxPreference {
	Single = "single",
	Multiple = "multiple"
}

export enum PaymentPreferences {
	AMOUNT = "amount",
	PERCENT = "percent",
	TOTAL = "total"
}

export interface PaymentPreferenceSettings {
	custom_percentage?: number | null;
	custom_amount?: number | null;
	is_optional?: boolean;
	payment_options?: PaymentOptions;
}

export class TaxPreferenceSettings {
	document_tax_settings: TaxPreference = TaxPreference.Single;
	document_tax_show_individual = false;
}

export class ProductPreferenceSettings {
	margin = 0;
	markup = 0;
}

export class DocumentEmailReminders {
	is_active = false;
	first_reminder_in = 0;
	to_repeat = false;
	repeat_every = 0;
}

export class DocumentPOPreferences {
	is_po_mandate_for_proposal = false;
	is_po_mandate_for_contract = false;
	is_po_mandate_for_quote = false;
}

export const DEFAULT_DOCUMENT_FONT_SIZE = "14px";
export const DEFAULT_DOCUMENT_FONT_FACE = "Source Sans Pro";
export const DEFAULT_DOCUMENT_FONT_COLOR = "#0c1d33";
export class DocumentDefaultDocument {
	font_size = DEFAULT_DOCUMENT_FONT_SIZE;
	font_face = DEFAULT_DOCUMENT_FONT_FACE;
	font_color = DEFAULT_DOCUMENT_FONT_COLOR;
	margin = {
		top: 25.4,
		bottom: 25.4,
		left: 25.4,
		right: 25.4
	};
}

export class QuoteBlockDefaultSettings {
	pricing_table_styles: PricingTableStyles = new PricingTableStyles();
	section_name: string | null = null;
	is_quote_block_compact = false;
	hide_tax = false;
	exclude_tax_from_total = false;
	highlighted_total: QuoteBlockHighlightedTotal =
		QuoteBlockHighlightedTotal.TOTAL_INCLUDING_TAX;
	color_placement: QuoteBlockColorPlacement =
		QuoteBlockColorPlacement.HEADER_AND_SIDES;
}

export class DocumentPreferenceSettings {
	expiration: DocumentExpirationPreference =
		new DocumentExpirationPreference();
	email_reminder: DocumentEmailReminders = new DocumentEmailReminders();
	default_from_address?: string | null = null;
	default_from_address_type?: string | null = null;
	default_document: DocumentDefaultDocument = new DocumentDefaultDocument();
	allow_download = true;
	is_nylas?: boolean;
	default_cc?: string[] | null;
	default_bcc?: string[] | null;
	quote_approval_message?: string | null = null;
	merge_tags?: MergeTagVariable[];
	risk_matrix: RiskMatrixBlockData = new RiskMatrixBlockData();
	is_new_quote_experienced?: boolean;
	is_quote_block_revamped?: boolean;
	is_quote_block_design_reverted_to_old?: boolean;
	po_mandatory_preferences?: DocumentPOPreferences =
		new DocumentPOPreferences();
	po_length?: number;
	default_zomentum_doc_paper_size?: DocumentPaperSize =
		defaultZomentumDocPaperSize;
	default_quote_block_settings?: QuoteBlockDefaultSettings;
}

export class OrderFormPreferenceSettings {
	pipeline_id?: string | null = null;
	create_opp_for_paid_orders: string | null = null;
	create_opp_for_unpaid_orders: string | null = null;
}

export class DocumentCommentBoxLabels {
	comment_box_header_label = "Add comments to the Document";
	seconds_label = "A few seconds ago";
	minutes_label = "minutes ago";
	hours_label = "hours ago";
	days_label = "days ago";
	comment_hint_label = "Enter your comment here";
	download_button_label = "Download";
	send_button_label = "Send";
	post_comment_label = "Post Comment";
	po_label = "Enter PO Number";
	sign_document_label = "Sign Document";
}
export class SignatureBlockLabels {
	signature_box_label = "Signature Box";
	to_be_signed_label = "To be signed";
}
export class UserSettings {
	notification_settings: NotificationSettings = new NotificationSettings();
	time_zone = "";
	time_zone_offset = 0;
	time_zone_display_browser_pop_up = false;
	preference_settings: PreferenceSettings | null = null;
	document_comment_box_labels?: DocumentCommentBoxLabels =
		new DocumentCommentBoxLabels();
	signature_block_labels?: SignatureBlockLabels = new SignatureBlockLabels();
	date_pattern? = "";
}

export interface PreferenceSettingsFormValues {
	default_expiration_checkbox: boolean;
	relative_expiration_date: number;
	warn_recipients_checkbox: boolean;
	default_reminder_checkbox: boolean;
	default_from_address: string;
	first_reminder: number;
	repeat_reminder_checkbox: boolean;
	repeat_reminder_value: number;
	mark_up: number;
	margin: number;
	document_tax_default_setting: TaxPreference;
	document_tax_show_individual: boolean;
	enable_payment_checkbox: boolean;
	payment_default_setting: string;
	is_payment_optional: boolean;
	custom_amount: number;
	payment_percent: number;
	payee_id?: string;
	default_email_tracking: boolean;
	opens?: boolean;
	links?: boolean;
	thread_replies?: boolean;
}

export class AddCompanyUserRequest {
	company_id = "";
	email = "";
	name: Name = new Name();
	phone = "";
	role: UserRole = UserRole.SecondaryRole;
	job_title = "";
	access_level?: UserAccess;
	default_owner_id?: string = "";
	role_id?: string;
}

export class CompanyUsersRequest {
	filters: TableQueryFilter[];
	keywords: string | null;

	constructor(filters: TableQueryFilter[], keywords: string | null) {
		this.filters = filters;
		this.keywords = keywords;
	}
}

export class CompanyUsersResponse {
	base = "";
	filtered_count_map: CompanyUserCount = new CompanyUserCount();
	data: UserData[] = new Array<UserData>();
}

export class CompanyUserFormValues {
	first_name = "";
	last_name = "";
	email = "";
	phone = "";
	role: UserRole = UserRole.SecondaryRole;
	access_level?: UserAccess;
	job_title = "";
	default_owner?: string = "";
	role_id?: string = "";
}

export enum InitializationStep {
	IntentCreated = "intent_created",
	PasswordResetDone = "password_reset_done",
	CompanyDetailsCaptured = "company_details_captured",
	PaymentScreenPresented = "payment_screen_presented",
	RequestedToJoinCompany = "requested_to_join_company"
}

export enum DefaultProductSourceType {
	All = "all",
	SelfCatalog = "self_catalog",
	Icecat = "icecat",
	CustomDistributor = "custom_distributor",
	DickerData = "dicker_data"
}

export const DefaultProductSourceOptions = [
	{ label: "All", value: DefaultProductSourceType.All },
	{ label: "Self Catalog", value: DefaultProductSourceType.SelfCatalog },
	{
		label: "Global (Formerly Etilize)",
		value: DefaultProductSourceType.Icecat
	},
	{
		label: "Custom Distributor",
		value: DefaultProductSourceType.CustomDistributor
	},
	{ label: "Dicker Data", value: DefaultProductSourceType.DickerData }
];

export class UserIntegrationConfiguration {
	client_id = "";
	client_secret = "";
	connectwise: ConnectWiseIntegration = new ConnectWiseIntegration();
	email: EmailIntegration = new EmailIntegration();
	autotask: AutotaskIntegration = new AutotaskIntegration();
	zapier: ZapierIntegration = new ZapierIntegration();
	kaseya: KaseyaIntegration = new KaseyaIntegration();
}

export class CompanyIntegrationConfiguration {
	connectwise: ConnectWiseIntegration = new ConnectWiseIntegration();
	email: EmailIntegration[] = new Array<EmailIntegration>();
	autotask: AutotaskIntegration = new AutotaskIntegration();
	zapier: ZapierIntegration = new ZapierIntegration();
}

export class CompanyConfiguration {
	integrations: CompanyIntegrationConfiguration =
		new CompanyIntegrationConfiguration();
}

export class UserConfiguration {
	integrations: UserIntegrationConfiguration =
		new UserIntegrationConfiguration();
	notification: NotificationConfiguration = new NotificationConfiguration();
}
export enum CompanyJoinIntentStatus {
	REQUESTED = "requested",
	REJECTED = "rejected"
}
export interface CompanyJoinIntent {
	id: string;
	created: Date;
	updated: Date;
	companyId: string;
	userId: string;
	status: CompanyJoinIntentStatus;
	requested_company_id: string;
	requested_company: CompanyPublicData;
}
export class ConfigurationSettingsResponse {
	company_configs: CompanyConfiguration = new CompanyConfiguration();
	domain = "";
	is_associated_with_vendors_other_than_default = false;
	user_configs: UserConfiguration = new UserConfiguration();
	company_initialization_step?: InitializationStep;
	user_initialization_step?: InitializationStep;
	sample_data_company_id?: string;
	signup_initiating_vendor?: string;
	is_user_onboarding_complete?: boolean | null;
	company_join_intent?: CompanyJoinIntent;
	skip_onboarding?: boolean;
	afterLoginRedirectTo?: string;
	users?: { data: UserData[] };
	company?: UserCompany;
	current_user?: UserData;
}

export enum LibraryEntityItem {
	Image = "image",
	Video = "video",
	Block = "block"
}

interface BlockData {
	data: Block[];
}

export interface LibraryItem {
	id: string;
	display_name: string;
	url: string;
	entity_id: string;
	entity_type: LibraryEntityItem;
	block_type: BlockType | null;
	company_id: string | null;
	vendor_details?: VendorCompany;
	thumbnail?: string;
	blocks: BlockData | null;
	tag_ids?: string[];
	is_single_block?: boolean;
	user_id?: string;
	user?: UserData;
	created?: string;
	updated?: string;
}

export interface EmailAliasItem {
	data: EmailAlias[];
	integrations: Integrations;
}

export interface VendorEmailAliasItem {
	data: EmailAlias[];
	integrations: VendorUserIntegration;
}

export interface GetLibraryResponse {
	data: LibraryItem[];
	filtered_count_map: { all: number } | null;
}

export interface GetLibraryRequest {
	readonly included_child_entities?: ChildEntityHydrationParams[];
	readonly filters?: TableQueryFilter[];
}

export interface CreateLibraryItemRequest {
	display_name: string;
	url?: string;
	entity_id?: string;
	entity_type: LibraryEntityItem;
	is_single_block?: boolean;
	tag_ids?: string[];
}

export interface UpdateLibraryItemRequest {
	display_name?: string;
	is_single_block?: boolean;
	tag_ids?: string[];
	entity_id?: string;
}

export interface EmailAliasRequest {
	alias: {
		name: string;
		email: string;
	};
}

export enum CardFormInputType {
	Name = "name",
	Number = "number",
	Expiry = "expiry",
	CVV = "cvv",
	Zip = "zip",
	State = "state"
}

export interface UserCalendar {
	id: string;
	description: string;
	name: string;
	read_only: boolean;
	account_id: string;
}

export interface UserCalendarResponse {
	data: UserCalendar[];
	filtered_count_map: null;
}

export interface ChargebeeInvoice {
	readonly invoice_id: string;
	readonly date: Date;
	readonly download_url?: string;
	readonly next_offset?: string[];
}

export interface ChargebeeInvoiceResponse {
	readonly data: ChargebeeInvoice[];
	readonly filtered_count_map?: { readonly all: number };
}

export interface ChargebeeCreditNote {
	readonly date: Date;
	readonly invoice_id: string;
	readonly credit_note_id: string;
	readonly next_offset?: string[];
}

export interface ChargebeeCreditNoteResponse {
	readonly data: ChargebeeCreditNote[];
	readonly filtered_count_map?: { readonly all: number };
}

export interface ChargebeeCreditNoteDetail {
	readonly credit_note_id: string;
	readonly download_url: string;
}

export enum CancellationReason {
	Engineering = "engineering",
	ProductFeatures = "product_features",
	ProductCompetition = "product_competition",
	Success = "success",
	Usage = "usage",
	Other = "other"
}

export interface PauseChargebeeSubscriptionRequest {
	readonly request_cancellation_reason: CancellationReason;
	readonly message?: string;
	readonly cancellation_requested_date?: Date;
}

export interface AddTaxCategoryRequest {
	readonly name: string;
	readonly identifier: string;
	readonly default_for_types: ProductType[];
	readonly disabled: boolean;
	readonly tax_codes: TaxCode[];
}

export interface AddTaxCategoryFormValues {
	readonly name: string;
	readonly identifier: string;
	readonly default_for_types: ProductType[];
	readonly disabled: boolean;
	readonly tax_codes: Record<string, TaxCodeLevel[]>;
}

export interface AddTaxRegionFormValues {
	readonly name: string;
	readonly disabled: boolean;
	readonly is_default: boolean;
}

export interface AddTaxRegionRequest {
	readonly name: string;
	readonly disabled: boolean;
	readonly is_default: boolean;
}

export interface TaxCategoriesResponse {
	readonly data: TaxCategory[];
}

export interface TaxCodeLevel {
	name: string;
	tax_percentage: number;
}

export interface TaxCode {
	readonly levels: TaxCodeLevel[];
	readonly region: TaxRegion;
	readonly region_id: string;
}

export interface TaxCategory {
	readonly id: string;
	readonly name: string;
	readonly identifier: string;
	readonly default_for_types: ProductType[];
	readonly disabled: boolean;
	readonly tax_codes: TaxCode[];
}

export interface TaxRegion {
	readonly id: string;
	readonly name: string;
	readonly disabled: boolean;
	readonly is_default: boolean;
}

export interface TaxRegionsResponse {
	readonly data: TaxRegion[];
}

export interface VerifyEmailRequest {
	readonly email: string;
}

export interface AddCustomDomainContract {
	readonly domain: string;
	readonly connected_emails: ConnectedEmails[];
	readonly mail_from_sub_domain?: string;
}

export interface CustomDomainRecord {
	readonly recordName: string;
	readonly recordType: string;
	readonly recordValue: string;
}

export interface CustomDomainItem {
	readonly domain: string;
	readonly records?: CustomDomainRecord[];
	readonly mail_from_records?: CustomDomainRecord[];
}

export class EmailSignature {
	name = "";
	body = "";
	is_default = false;
}

export class UserEmailSignature {
	signatures: EmailSignature[] = new Array<EmailSignature>();
}

export enum PricingAddOnStatus {
	ENABLED = "enabled",
	DISABLED = "disabled",
	ALWAYS_ENABLED = "always_enabled",
	ALWAYS_DISABLED = "always_disabled"
}

export enum PricingAddOnEnum {
	DKIM = "dkim",
	MULTI_FACTOR_AUTHENTICATION = "multi-factor-authentication",
	API_ACCESS = "api_access",
	CUSTOM_DOMAIN = "custom-domain",
	SSO = "sso",
	AMAZON_BUSINESS = "amazon",
	CONNECTBOOSTER = "connect_booster",
	STRIPE = "stripe"
}

export enum PricingAddOnInterval {
	RECURRING = "RECURRING",
	NON_RECURRING = "NON_RECURRING",
	RECURRING_TEXT = "recurring",
	ONE_TIME_TEXT = "one-time"
}

export enum PricingPlanNames {
	LAUNCH = "launch",
	EXPAND = "expand",
	GROWTH = "growth",
	PRO = "pro",
	ENTERPRISE = "enterprise",
	IGNITE = "ignite",
	ONE_MAN_SHOP = "one-man-shop",
	BUSINESS = "business"
}

export const higherSubscriptionPlans = [
	PricingPlanNames.EXPAND,
	PricingPlanNames.GROWTH
];
export const lowerSubscriptionPlans = [
	PricingPlanNames.IGNITE,
	PricingPlanNames.LAUNCH
];

export const newSubscriptionPlans = [
	...lowerSubscriptionPlans,
	...higherSubscriptionPlans
];

export const oldSubscriptionPlans = [
	PricingPlanNames.ONE_MAN_SHOP,
	PricingPlanNames.BUSINESS
];

export interface PricingAddOn {
	name: string;
	id: string;
	status: string;
	price: number;
	billing_interval?: string;
	description?: string;
}

export interface PricingAddOnProps {
	readonly isEnabled: string;
	readonly title: string;
	readonly subtitle: string;
	readonly price: number;
	readonly onChange: (name: string, status: string) => void;
}

export enum PricingAddOnUpdate {
	ADD = "Add",
	REMOVE = "Remove"
}

export interface PricingAddOnSummary {
	id: string;
	amount: number;
}

export enum PricingPlanTierEnum {
	MONTHLY = "monthly",
	ANNUAL = "annual",
	YEARLY = "yearly",
	QUARTERLY = "quarterly"
}

export const PricingPlanTierOptions = [
	{
		label: "Monthly",
		value: PricingPlanTierEnum.MONTHLY,
		disabled: false
	},
	{
		label: "Quarterly",
		value: PricingPlanTierEnum.QUARTERLY,
		disabled: false
	},
	{
		label: "Yearly",
		value: PricingPlanTierEnum.ANNUAL,
		disabled: false
	}
];

export interface PricingPlanDisableSeatUpdation {
	action: string;
	disabled: boolean;
}

export interface PricingPlanEachTier {
	starting_unit: number;
	ending_unit: number;
	price: number;
}

export interface PricingPlanTiers {
	period: string;
	tier: Array<PricingPlanEachTier>;
	active: boolean;
}

export interface PricingPlanDowngradeDescription {
	plan: PricingPlanNames;
	downgrade_description: string[];
}

export class PricingPlan {
	name = "";
	add_ons: PricingAddOn[] = new Array<PricingAddOn>();
	plan_tiers: PricingPlanTiers[] = new Array<PricingPlanTiers>();
	hierarchy = 0;
	current_plan = false;
	currency = "";
	description: string[] = [];
	downgrade_descriptions: PricingPlanDowngradeDescription[] = [];
}

export interface PricingPlanProps {
	readonly price: string;
	readonly planTier?: PricingPlanTiers;
	readonly isSelected: boolean;
	readonly currentPlan: PricingPlan;
	readonly selectedPlanHierarchy: number;
	readonly summary: PricingPlanSummary;
	readonly plan: PricingPlan;
	readonly currency: string;
	readonly disableSeatUpdation: PricingPlanDisableSeatUpdation;
	readonly totalQuantityOfSeats: number;
	readonly selectedPlanName: string;
	readonly updateDeactivatedSeats: (seats: string[]) => void;
	readonly removeSeats: (isRemove: boolean) => void;
	readonly updateTotalQuantityOfSeats: (seats: number) => void;
	readonly updateDisableRemoveSeats: (isDisable: boolean) => void;
	readonly updateAdditionalSeats: (seats: number) => void;
	readonly estimate: (estimate: PricingPlanEstimate) => void;
	readonly onChangePlan: (e: RadioChangeEvent) => void;
}

export interface PricingPlanUserActionProps {
	readonly additionalSeatsPrice: number;
	readonly seatPrice: number;
	readonly seats: number;
	readonly currency: string;
	readonly disableSeatsUpdation: PricingPlanDisableSeatUpdation;
	readonly isDeactivateSeats: boolean;
	readonly totalSeatsFromSummary: number;
	readonly showActiveSeatsTag: boolean;
	readonly removeActiveSeatsCount: number;
	readonly onSeatsChange: (value: number) => void;
	readonly updateDeactivatedSeats: (seats: string[]) => void;
}

export interface PricingPlanResponse {
	data: PricingPlan[];
}

export interface PricingPlanSummary {
	company_id: string;
	billing_amount: number;
	total_quantity: number;
	next_billing_date: Date;
	plan_id: string | null;
	addons: PricingAddOnSummary[];
	hide_yearly_tier: boolean;
}

export class PricingSummary implements PricingPlanSummary {
	company_id = "";
	billing_amount = 0;
	total_quantity = 0;
	next_billing_date = new Date(new Date().toISOString());
	plan_id = null;
	addons = new Array<PricingAddOnSummary>();
	hide_yearly_tier = false;
}

export interface PricingPlanSummaryProps {
	readonly onChangePlanTier: (e: RadioChangeEvent) => void;
	readonly planTier: string;
	readonly currentPlan: PricingPlan;
	readonly billingAmount: number;
	readonly totalQuantityOfSeats: number;
	readonly summary: PricingPlanSummary;
	readonly planUpdate: string;
	readonly isRemoveSeat: boolean;
	readonly isSeatAddition: boolean;
	readonly updateAddOnText: string;
	readonly deactivatedSeats: string[];
	readonly isRemoveSeatDisabled: boolean;
}

export interface PricingPlanSummaryResponse {
	data: PricingSummary;
}

export interface SubscriptionOverviewResponse {
	plan_details: PricingPlanResponse;
	subscription_details: PricingPlanSummaryResponse | null;
	billing_info: BillingInfo;
}

export interface UpdatePricingPlan {
	plan: string;
	period: string;
	addon_ids: string[];
}

export class UpdatePricingPlanRequest implements UpdatePricingPlan {
	plan = "";
	period = "";
	addon_ids = new Array<string>();
}

export enum UpdatePricingPlanConstants {
	UPGRADE = "upgrade",
	DOWNGRADE = "downgrade",
	PLAN_UPDATE = "plan-update",
	TIER_UPDATE = "tier-update",
	UPGRADED = "upgraded",
	DOWNGRADED = "downgraded",
	ADDON_UPDATE = "addon-update",
	ADD_ADDON = "add-addon",
	REMOVE_ADDON = "remove-addon"
}

export interface PricingPlanUserMeta {
	extra_quantity: number;
	removed_user_ids: string[];
}

export class PricingPlanUserRequest implements PricingPlanUserMeta {
	extra_quantity = 0;
	removed_user_ids = new Array<string>();
}

export enum PricingPlanId {
	IGNITE_ANNUAL = "ignite-annual",
	LAUNCH_MONTHLY = "launch-monthly",
	LAUNCH_ANNUAL = "launch-annual",
	LAUNCH_QUARTERLY = "launch-quarterly",
	EXPAND_MONTHLY = "expand-monthly",
	EXPAND_ANNUAL = "expand-annual",
	EXPAND_QUARTERLY = "expand-quarterly",
	GROWTH_MONTHLY = "growth-monthly",
	GROWTH_QUARTERLY = "growth-quarterly",
	GROWTH_ANNUAL = "growth-annual"
}

export interface PricingPlanEstimate {
	plan: string;
	period: string;
	no_of_seats: number;
	addons?: string[];
}

export enum PricingPlanEstimateAmountSummaryEntities {
	ADDON = "ADDON"
}

export class PricingPlanEstimateAmountSummary {
	entity_type = "";
	entity_id = "";
	amount = 0;
	seats = 0;
}

export class PricingPlanEstimateCreditNotes {
	amount_used = 0;
	amount_available = 0;
	total = 0;
}

export class PricingPlanEstimateAdditionalSeats {
	total_seats = 0;
	seat_amount = 0;
	starting_unit = 0;
	total_seat_amount = 0;
	ending_unit = null;
}

export class PricingPlanEstimateSavingOnOtherPeriod {
	period = "";
	percentage = 0;
	amount = 0;
}

export class PricingPlanEstimateSaving {
	from_period = "";
	saving_on_other_period =
		new Array<PricingPlanEstimateSavingOnOtherPeriod>();
}

export class PricingPlanEstimateData {
	next_billing_date = new Date(new Date().toISOString());
	currency = "";
	company_id = "";
	plan_id = "";
	plan = "";
	period = "";
	prorated_amount? = 0;
	plan_amount_summary = new Array<PricingPlanEstimateAmountSummary>();
	addon_ids? = new Array<string>();
	total_seats = 0;
	base_seats = 0;
	credit_notes? = new Array<PricingPlanEstimateCreditNotes>();
	next_billing_amount = null;
	additional_seat? = new PricingPlanEstimateAdditionalSeats();
	saving_on_yearly_plan = new Array<PricingPlanEstimateSaving>();
}

export class PricingPlanEstimateRequest implements PricingPlanEstimate {
	plan = "";
	period = "";
	no_of_seats = 0;
	addons? = new Array<string>();
}

export enum PricingPlanEstimateSummaryEntity {
	PLAN = "PLAN",
	ADDON = "ADDON"
}

export interface ZomentumBillingData {
	billingInfo: BillingInfo;
	chargebeeInvoicesList: ChargebeeInvoiceResponse;
	chargebeeCreditNotesList: ChargebeeCreditNoteResponse;
}

export interface RoleUsersCount {
	[key: string]: number;
}

export interface RoleApiPermission {
	name: string;
	entity: string;
	permissions: Permission[];
}

export interface Permission {
	name: string;
	access: string;
	api_permission: string;
	description: string;
	is_enabled: boolean;
	is_editable: boolean;
	entity: string;
	dependency: null | string[];
	public: boolean;
}

export enum AllRolePermissions {
	// opportunity
	OPPORTUNITY_CREATE = "opportunity_create",
	OPPORTUNITY_READ = "opportunity_read",
	OPPORTUNITY_ASSIGN = "opportunity_assign",
	OPPORTUNITY_BULK_DELETE = "opportunity_bulk_delete",
	OPPORTUNITY_BULK_UPDATE = "opportunity_bulk_update",
	OPPORTUNITY_IMPORT = "opportunity_import",
	OPPORTUNITY_MERGE = "opportunity_merge", // not yet done as of 09/21
	OPPORTUNITY_EDIT_ALL = "opportunity_edit_all",
	OPPORTUNITY_DELETE_ALL = "opportunity_delete_all",
	// client company & contact
	CLIENT_COMPANY_CREATE = "client_company_create",
	CLIENT_COMPANY_READ = "client_company_read",
	CLIENT_COMPANY_ASSIGN = "client_company_assign",
	CLIENT_COMPANY_BULK_DELETE = "client_company_bulk_delete",
	CLIENT_COMPANY_BULK_UPDATE = "client_company_bulk_update",
	CLIENT_COMPANY_IMPORT = "client_company_import",
	CLIENT_COMPANY_MERGE = "client_company_merge", // not yet done as of 09/21
	CLIENT_COMPANY_EDIT_ALL = "client_company_edit_all",
	CLIENT_COMPANY_DELETE_ALL = "client_company_delete_all",

	// document
	DOCUMENT_CREATE = "document_create",
	DOCUMENT_READ = "document_read",
	DOCUMENT_EDIT_ALL = "document_edit_all",
	DOCUMENT_DELETE_ALL = "document_delete_all",
	DOCUMENT_SEND = "document_send",
	DOCUMENT_ASSIGN = "document_assign",
	DOCUMENT_BULK_UPDATE = "document_bulk_update",
	DOCUMENT_BULK_DELETE = "document_bulk_delete",
	DOCUMENT_SHARE = "document_share",

	// document: signature block
	SIGNATURE_BLOCK_READ = "signature_block_read",

	// document: templates
	TEMPLATE_CREATE = "template_create",
	TEMPLATE_UPDATE = "template_update",
	TEMPLATE_READ = "template_read",
	// document: quote block
	QUOTE_BLOCK_ADD = "quote_block_add",
	QUOTE_BLOCK_UPDATE = "quote_block_update",
	QUOTE_BLOCK_EDIT_ALL = "quote_block_edit_all",
	// document: quote block bundle
	QUOTE_BLOCK_BUNDLE_ADD = "quote_block_bundle_add",
	QUOTE_BLOCK_BUNDLE_UPDATE = "quote_block_bundle_update",
	QUOTE_BLOCK_BUNDLE_EDIT_ALL = "quote_block_bundle_edit_all",
	// document: qbr
	QBR_CREATE = "qbr_create",
	QBR_READ = "qbr_read",
	QBR_EDIT_ALL = "qbr_edit_all",
	QBR_DELETE_ALL = "qbr_delete_all",
	QBR_ASSIGN = "qbr_assign",
	QBR_SEND = "qbr_send",
	QBR_SHARE = "qbr_share",
	// document: cloud assessment
	MS365_ASSESSMENT_READ = "ms365_assessment_read",
	MS365_ASSESSMENT_CREATE = "ms365_assessment_create",
	// document: sales assessment
	ASSESSMENT_CREATE = "assessment_create",
	ASSESSMENT_DELETE_ALL = "assessment_delete_all",
	ASSESSMENT_READ = "assessment_read",
	ASSESSMENT_EDIT_ALL = "assessment_edit_all",
	ASSESSMENT_ASSIGN = "assessment_assign",
	ASSESSMENT_SEND = "assessment_send",
	ASSESSMENT_SHARE = "assessment_share",
	// reports
	REPORT_CREATE = "report_create",
	REPORT_READ = "report_read",
	REPORT_EDIT_ALL = "report_edit_all",
	REPORT_DELETE_ALL = "report_delete_all",
	// sales automations
	SALES_AUTOMATION_CREATE = "sales_automation_create",
	SALES_AUTOMATION_UPDATE = "sales_automation_update",
	SALES_AUTOMATION_DELETE = "sales_automation_delete",
	SALES_AUTOMATION_READ = "sales_automation_read",
	// pipelines
	PIPELINE_CREATE = "pipeline_create",
	PIPELINE_READ = "pipeline_read",
	PIPELINE_UPDATE = "pipeline_update",
	PIPELINE_DELETE = "pipeline_delete",
	PIPELINE_VIEW = "pipeline_view",
	// products
	ITEM_CREATE = "item_create",
	ITEM_UPDATE = "item_update",
	ITEM_READ = "item_read",
	ITEM_DELETE = "item_delete",
	ITEM_IMPORT = "item_import",
	ITEM_MERGE = "item_merge", // not yet done as of 09/21
	ITEM_BULK_DELETE = "item_bulk_delete",
	ITEM_BULK_UPDATE = "item_bulk_update",
	// custom fields
	CUSTOM_FIELD_CREATE = "custom_field_create",
	CUSTOM_FIELD_READ = "custom_field_read",
	CUSTOM_FIELD_UPDATE = "custom_field_update",
	CUSTOM_FIELD_DELETE = "custom_field_delete",
	CUSTOM_FIELD_VIEW = "custom_field_view",
	// company
	COMPANY_CREATE = "company_create",
	COMPANY_READ = "company_read",
	COMPANY_UPDATE = "company_update",
	// sales activity
	SALES_ACTIVITY_ASSIGN = "sales_activity_assign",
	SALES_ACTIVITY_CREATE = "sales_activity_create",
	SALES_ACTIVITY_READ = "sales_activity_read",
	SALES_ACTIVITY_DELETE_ALL = "sales_activity_delete_all",
	SALES_ACTIVITY_EDIT_ALL = "sales_activity_edit_all",
	SALES_ACTIVITY_BULK_UPDATE = "sales_activity_bulk_update",
	// tax setup
	TAX_SETUP_READ = "tax_setup_read",
	TAX_SETUP_UPDATE = "tax_setup_update",
	// system email templates
	EMAIL_TEMPLATE_READ = "email_template_read",
	EMAIL_TEMPLATE_UPDATE = "email_template_update",
	EMAIL_UPDATE = "email_setting_update",
	// dkim
	DKIM_UPDATE = "dkim_update",
	// async operation
	ASYNC_OPERATION_READ = "async_operation_read",
	ASYNC_OPERATION_CREATE = "async_operation_create",
	// users
	USER_READ = "user_read",
	USER_UPDATE = "user_update",
	// approvals
	APPROVAL_AUTOMATION_CREATE = "approval_automation_create",
	APPROVAL_AUTOMATION_READ = "approval_automation_read",
	APPROVAL_AUTOMATION_ACCEPT = "approval_automation_accept",
	// integrations
	COMPANY_CONNECT = "integration_connect",
	// preferences
	PREFERENCE_UPDATE = "preference_update",
	// accounts
	ACCOUNT_READ = "account_read",
	ACCOUNT_UPDATE = "account_update",
	// billing
	BILLING_UPDATE = "billing_update",
	// audit log
	AUDIT_LOG_READ = "audit_log_read",
	// mcp audit log
	MCP_AUDIT_LOG_READ = "mcp_tool_call_log_read",
	MCP_AGENT_MANAGE = "mcp_agent_edit_all",

	//integrations
	DISTRIBUTOR_CONNECT = "distributor_connect",
	PAYMENT_INTERFACE_CONNECT = "payment_interface_connect",
	ZAPIER_CONNECT = "zapier_connect",

	//supplier management
	SUPPLIER_READ = "supplier_read",
	SUPPLIER_CREATE = "supplier_create",
	SUPPLIER_UPDATE = "supplier_update"
}

const initialRoutes = Object.values(ZomentumRoutes).reduce((prev, route) => {
	return { ...prev, [route]: null };
}, {} as Record<ZomentumRoutes, null>);

// Every route is mapped to a permission. If the permission is enabled, route will be accessible. For routes which do not require any permission are mapped to NULL.
export const ZomentumRouteToPermissionMap: Record<
	string,
	string | string[] | null
> = {
	...initialRoutes,
	[ZomentumRoutes.AccountSettings]: AllRolePermissions.ACCOUNT_READ,
	[ZomentumRoutes.Assessments]: AllRolePermissions.ASSESSMENT_READ,
	[ZomentumRoutes.AuditLogs]: AllRolePermissions.AUDIT_LOG_READ,
	[ZomentumRoutes.AutoTaskIntegration]: AllRolePermissions.COMPANY_CONNECT,
	[ZomentumRoutes.Billing]: AllRolePermissions.BILLING_UPDATE,
	[ZomentumRoutes.BulkActionResultPage]:
		AllRolePermissions.ASYNC_OPERATION_READ,
	[ZomentumRoutes.CancelSubscription]: AllRolePermissions.ACCOUNT_READ,
	[ZomentumRoutes.ClientsDetail]: AllRolePermissions.CLIENT_COMPANY_READ,
	[ZomentumRoutes.ClientsList]: AllRolePermissions.CLIENT_COMPANY_READ,
	[ZomentumRoutes.ContactsList]: AllRolePermissions.CLIENT_COMPANY_READ,
	[ZomentumRoutes.ConnectWiseIntegration]: AllRolePermissions.COMPANY_CONNECT,
	[ZomentumRoutes.CreateReport]: AllRolePermissions.REPORT_CREATE,
	[ZomentumRoutes.CustomFieldsList]: AllRolePermissions.CUSTOM_FIELD_VIEW,
	[ZomentumRoutes.CustomFieldsSettings]: AllRolePermissions.CUSTOM_FIELD_VIEW,
	[ZomentumRoutes.DocumentApproval]:
		AllRolePermissions.APPROVAL_AUTOMATION_READ,
	[ZomentumRoutes.DocumentApprovalCreatePage]:
		AllRolePermissions.APPROVAL_AUTOMATION_CREATE,
	[ZomentumRoutes.DocumentApprovalDetailsPage]:
		AllRolePermissions.APPROVAL_AUTOMATION_CREATE,
	[ZomentumRoutes.DocumentsDetail]: [
		AllRolePermissions.DOCUMENT_READ,
		AllRolePermissions.QBR_READ,
		AllRolePermissions.ASSESSMENT_READ
	],
	[ZomentumRoutes.DocumentsList]: [
		AllRolePermissions.DOCUMENT_READ,
		AllRolePermissions.QBR_READ,
		AllRolePermissions.ASSESSMENT_READ
	],
	[ZomentumRoutes.DocumentsPdfView]: [
		AllRolePermissions.DOCUMENT_READ,
		AllRolePermissions.QBR_READ,
		AllRolePermissions.ASSESSMENT_READ
	],
	[ZomentumRoutes.DocumentsPreview]: [
		AllRolePermissions.DOCUMENT_READ,
		AllRolePermissions.QBR_READ,
		AllRolePermissions.ASSESSMENT_READ
	],
	[ZomentumRoutes.EmailSettings]: AllRolePermissions.EMAIL_UPDATE,
	[ZomentumRoutes.EmailTemplateSettings]:
		AllRolePermissions.EMAIL_TEMPLATE_READ,
	[ZomentumRoutes.InfusionSoftIntegration]:
		AllRolePermissions.COMPANY_CONNECT,
	[ZomentumRoutes.IntegrationSettings]: AllRolePermissions.COMPANY_CONNECT,
	[ZomentumRoutes.InvoicesList]: AllRolePermissions.ACCOUNT_READ,
	[ZomentumRoutes.KaseyaIntegration]: AllRolePermissions.COMPANY_CONNECT,
	[ZomentumRoutes.McpAgentDetail]: AllRolePermissions.MCP_AUDIT_LOG_READ,
	[ZomentumRoutes.McpAgents]: AllRolePermissions.MCP_AUDIT_LOG_READ,
	[ZomentumRoutes.Ms365AssessmentSuccess]:
		AllRolePermissions.MS365_ASSESSMENT_READ,
	[ZomentumRoutes.OpportunityDetail]: AllRolePermissions.OPPORTUNITY_READ,
	[ZomentumRoutes.OpportunityList]: AllRolePermissions.OPPORTUNITY_READ,
	[ZomentumRoutes.PipelineSettings]: AllRolePermissions.PIPELINE_VIEW,
	[ZomentumRoutes.ProductsDetail]: AllRolePermissions.ITEM_READ,
	[ZomentumRoutes.ProductsList]: AllRolePermissions.ITEM_READ,
	[ZomentumRoutes.RepairShoprIntegrationPage]:
		AllRolePermissions.COMPANY_CONNECT,
	[ZomentumRoutes.ReportsDetail]: AllRolePermissions.REPORT_CREATE,
	[ZomentumRoutes.ReportsList]: AllRolePermissions.REPORT_READ,
	[ZomentumRoutes.SalesActivityAutomationCreate]:
		AllRolePermissions.SALES_AUTOMATION_CREATE,
	[ZomentumRoutes.SalesActivityAutomationsClone]:
		AllRolePermissions.SALES_AUTOMATION_CREATE,
	[ZomentumRoutes.SalesActivityAutomationsEdit]:
		AllRolePermissions.SALES_AUTOMATION_UPDATE,
	[ZomentumRoutes.SalesActivityAutomationsList]:
		AllRolePermissions.SALES_AUTOMATION_READ,
	[ZomentumRoutes.SalesActivityPage]: AllRolePermissions.SALES_ACTIVITY_READ,
	[ZomentumRoutes.SyncroIntegration]: AllRolePermissions.COMPANY_CONNECT,
	[ZomentumRoutes.TaxRegionSettings]: AllRolePermissions.TAX_SETUP_READ,
	[ZomentumRoutes.TemplatesDetail]: AllRolePermissions.TEMPLATE_CREATE,
	[ZomentumRoutes.TemplatesList]: AllRolePermissions.TEMPLATE_READ,
	[ZomentumRoutes.TimezoneSettings]: AllRolePermissions.ACCOUNT_READ,
	[ZomentumRoutes.UserSettings]: AllRolePermissions.USER_READ,
	[ZomentumRoutes.Suppliers]: AllRolePermissions.SUPPLIER_READ
};

export interface GoogleDrivePreferenceSettings {
	invite_sent: boolean;
	used_documents_count: number;
}

export class PreferenceSettings {
	document_preference_settings?: DocumentPreferenceSettings =
		new DocumentPreferenceSettings();
	product_preference_settings: ProductPreferenceSettings =
		new ProductPreferenceSettings();
	tax_preference_settings: TaxPreferenceSettings =
		new TaxPreferenceSettings();
	payment_preference_settings?: PaymentPreferenceSettings | null;
	email_tracking_preference_settings?: EmailTracking | null;
	google_drive_preference_settings?: GoogleDrivePreferenceSettings | null;
	kanban_preference_settings?: KanabanPreferenceSettings;
	order_form_preference_settings?: OrderFormPreferenceSettings | null;
	default_product_source?: DefaultProductSourceType;
}

export class UserData {
	company_id = "";
	role: UserRole | null = null;
	access_level?: UserAccess;
	roles: UserPermissionRole[] = new Array<UserPermissionRole>();
	roles_permissions?: UserRolePermissions = new UserRolePermissions();
	created: Date = new Date();
	email = "";
	fc_token = "";
	id = "";
	document_last_used_from_address = "";
	integrations: Integrations = new Integrations();
	name: Name = new Name();
	phone = "";
	updated: Date = new Date();
	user_settings: UserSettings = new UserSettings();
	is_disabled = false;
	bot_user_source: string | null = null;
	job_title = "";
	role_id?: string = "";
	is_save_view_tutorial_shown?: boolean = false;
	zomentum_payment_lead_collected?: boolean = false;
	zomentum_payment_connect_now_lead_collected?: boolean = false;
	is_owner?: boolean = false;

	public static getFullName = (
		user: UserData,
		showInActive?: boolean
	): string => {
		const name = capitalizeFirstLetter(user.name.full);
		if (showInActive) return `${name} (Inactive user)`;
		return name;
	};

	public static getFullNameWithEmail = (
		user: UserData,
		showInActive?: boolean
	): string => {
		const fullName = UserData.getFullName(user);
		const email = !!user.email.length ? `<${user.email}>` : "";
		if (showInActive) return `${fullName} ${email} (Inactive user)`;
		return `${fullName} ${email}`;
	};
}

export interface HubspotUserTokenResponse {
	token: string;
}

export enum PendingUsersModalMode {
	REQUEST = "REQUEST",
	ADD = "ADD",
	REJECT = "REJECT"
}

export interface PendingUserDetail {
	id: string;
	created: Date;
	updated: Date;
	email: string;
	name: Name;
}

export interface PendingUser {
	id: string;
	created: Date;
	updated: Date;
	company_id: string;
	user_id: string;
	sample_data_company_id?: string;
	signup_initiating_vendor: string | null;
	user: PendingUserDetail;
	vendor_company: VendorCompany;
	is_vendor_accepted: boolean;
}

export interface PendingUserResponse {
	data: PendingUser[];
}
export class CompanyPublicData {
	id = "";
	name = "";
	logo = "";
	favicon = "";
	loading_icon = "";
	domain = "";
	created_time: Date = new Date();
	updated_time: Date = new Date();
	user_count = 0;
}
export interface CompanyPublicDataResponse {
	data: CompanyPublicData[];
}
export interface GetConfigurationRequest {
	readonly included_child_entities: ChildEntityHydrationParams[];
}
